import { Component, HostBinding, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT, NgIf } from '@angular/common';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { Subscription } from 'rxjs';
import { routerFlipTransition } from '../../core/animations/router.animations';
import { RouterHelper } from '../../core/utilities/router-helper';

@Component({
  selector: 'app-sso-app-callback',
  templateUrl: './sso-app-callback.component.html',
  styleUrls: ['./sso-app-callback.component.scss'],
  animations: [routerFlipTransition()],
  standalone: true,
  imports: [NgIf, RouterLink, MatButton],
})
export class SsoAppCallbackComponent implements OnDestroy {
  private static readonly autoOpenDelayMs = 50;

  @HostBinding('@routerTransition') routerTransition = '';

  title = 'TeleGo App Login';
  loginUrl: string;
  serverError = false;
  serverMessage = 'You can return to the app.';
  deepLinkUrl = '';
  canOpenApp = false;

  private paramsSubscription: Subscription;
  private autoOpenTimeoutId?: number;
  private autoOpenScheduled = false;

  constructor(
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const module = RouterHelper.getRouteByComponent('LoginComponent');
    this.loginUrl = module?.path || '/auth/login';

    this.paramsSubscription = this.route.queryParams.subscribe((params: Params) => {
      this.applyQueryParams(params);
    });
  }

  ngOnDestroy(): void {
    this.paramsSubscription?.unsubscribe();

    if (this.autoOpenTimeoutId !== undefined) {
      this.document.defaultView?.clearTimeout(this.autoOpenTimeoutId);
    }
  }

  openDeepLink(): void {
    if (!this.deepLinkUrl) {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    win.location.href = this.deepLinkUrl;
  }

  private applyQueryParams(params: Params): void {
    this.title = this.readParam(params, 'title') || 'TeleGo App Login';
    this.deepLinkUrl =
      this.readParam(params, 'deepLinkUrl') || this.readParam(params, 'deeplinkUrl') || this.readParam(params, 'redirectUrl');
    this.canOpenApp = !!this.deepLinkUrl;

    const rawStatus = this.readParam(params, 'status').toLowerCase();
    const oauthError = this.readParam(params, 'error');
    this.serverError = rawStatus === 'error' || !!oauthError;

    const explicitMessage = this.readParam(params, 'message') || this.readParam(params, 'error_description');
    this.serverMessage = explicitMessage || (this.serverError ? 'Authentication failed.' : 'You can return to the app.');

    this.scheduleAutoOpen();
  }

  private scheduleAutoOpen(): void {
    if (!this.canOpenApp || this.autoOpenScheduled) {
      return;
    }

    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    this.autoOpenScheduled = true;
    this.autoOpenTimeoutId = win.setTimeout(() => {
      this.openDeepLink();
    }, SsoAppCallbackComponent.autoOpenDelayMs);
  }

  private readParam(params: Params, name: string): string {
    const value = params[name];

    return typeof value === 'string' ? value.trim() : '';
  }
}

